import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ChatPanel from 'components/common/ChatPanel';
import AlertDialog from 'components/UI/AlertDialog';
import useCurrentUser from 'hooks/useCurrentUser';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { format } from 'rut.js';
import { useCreateDemochatClientMutation, useGetIsPrivateDemoQuery } from 'services/api/demochat';
import { NAVY, RADIUS_COIN, YELLOW } from 'styles/tokens';
import { capitalizeFirstLetterOfEachWord } from 'utils/capitalize';
import {
  deleteChatFromHistory,
  getChatHistory,
  getClientPhoneIdentifier,
  setClientPhoneIdentifier,
} from 'utils/chatStorage';
import getReCaptchaToken from 'utils/getReCaptchaToken';

import ChatHistorySidebar from './ChatHistorySidebar';

const BUSINESS_UNITS_WITH_FORM: string[] = [
  'salfa-nurturing',
  'pompeyo-chat',
  'auto-chat',
  'salfa-rent',
  'bruno-fritsch',
  'autoland-colombia',
];

const BUSINESS_UNITS_WITH_DNI: string[] = ['autoland-colombia'];

type BusinessUnitWithForm = (typeof BUSINESS_UNITS_WITH_FORM)[number];

const sourcesByBusinessUnit: Record<BusinessUnitWithForm, string[]> = {
  'bruno-fritsch': [
    'ZENDESK: api',
    'ZENDESK: Toyota importador',
    'ZENDESK: MG importador',
    'ZENDESK: Omoda & Jaecoo importador',
    'ZENDESK: Peugeot importador',
    'ZENDESK: Citroën importador',
    'ZENDESK: Opel importador',
    'ZENDESK: Hyundai importador',
    'ZENDESK: Stellantis',
    'BRUNO_FRITSCH_API: rechazados',
  ],
};

const brandsByBusinessUnit: Record<BusinessUnitWithForm, string[]> = {
  'bruno-fritsch': [
    'USADOS',
    'TOYOTA',
    'MG',
    'PEUGEOT',
    'CITROËN',
    'OPEL',
    'OMODA',
    'JAECOO',
    'HYUNDAI',
    'CHERY',
    'NISSAN',
    'BYD',
  ],
  'salfa-nurturing': ['JEEP', 'CHERY', 'RAM', 'MITSUBISHI', 'KGM', 'FIAT', 'JMC', 'GAC', 'EXEED', 'BYD'],
  'pompeyo-chat': ['OPEL', 'KIA', 'NISSAN', 'PEUGEOT', 'MG', 'GEELY', 'DFSK', 'SUBARU', 'CITROËN'],
  'auto-chat': ['OPEL', 'KIA', 'NISSAN', 'PEUGEOT', 'MG', 'GEELY', 'DFSK', 'SUBARU', 'CITROËN'],
  'salfa-rent': ['USADOS'],
  'autoland-colombia': [
    'VOLKSWAGEN',
    'MERCEDES_BENZ',
    'CUPRA',
    'KIA',
    'MAZDA',
    'FORD',
    'SUZUKI',
    'SEAT',
    'CITROEN',
    'CHERY',
  ],
};

const mapAttributes = (key: string) => {
  if (key === 'rut') {
    return 'Rut';
  }
  if (key === 'dni') {
    return 'DNI';
  }
  if (key === 'email') {
    return 'Email';
  }
  if (key === 'modelOfInterest') {
    return 'Modelo de Interés';
  }
  if (key === 'firstName') {
    return 'Nombre';
  }
  if (key === 'lastName') {
    return 'Apellido';
  }
  return key;
};

const NewDemochat: React.FC = () => {
  const { companyIdentifier } = useParams<{ companyIdentifier: string }>();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const { data: privateDemoData } = useGetIsPrivateDemoQuery({
    companyIdentifier: companyIdentifier ?? '',
  });
  if (privateDemoData?.isPrivateDemo) {
    if (!currentUser || currentUser.id === 0) {
      navigate(`/sign-in?redirect=/demochat/${companyIdentifier}`);
    }
  }

  let sources: string[] = [];
  let brands: string[] = [];
  if (companyIdentifier && BUSINESS_UNITS_WITH_FORM.includes(companyIdentifier)) {
    brands = brandsByBusinessUnit[companyIdentifier] || [];
    sources = sourcesByBusinessUnit[companyIdentifier] || [];
  }

  const [openForm, setOpenForm] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const useDni = companyIdentifier && BUSINESS_UNITS_WITH_DNI.includes(companyIdentifier);

  const [clientData, setClientData] = useState({
    firstName: '',
    lastName: '',
    ...(useDni ? { dni: '' } : { rut: '' }),
    email: '',
    modelOfInterest: '',
    brandOfInterest: brands.length > 0 ? brands[0] : '',
    source: sources.length > 0 ? sources[0] : '',
  });

  const clientPhoneIdentifier = getClientPhoneIdentifier(companyIdentifier);

  const [createDemochatClient, { data: createClientData, error }] = useCreateDemochatClientMutation();
  const [searchParams] = useSearchParams();

  // Chat history management
  const [chatHistory, setChatHistory] = useState(() => getChatHistory(companyIdentifier));

  if (!companyIdentifier) {
    navigate('/demochat');
    return null;
  }
  const showSidebar = companyIdentifier ? companyIdentifier.includes('relif') : false;

  const startChat = () => {
    getReCaptchaToken()
      .then((recaptchaToken) => {
        createDemochatClient({ companyIdentifier, recaptchaToken, clientData }).catch((e) => {
          console.error('Error creating demo chat client', e);
          setOpenError(true);
        });
      })
      .catch((e) => {
        console.error('Error getting recaptcha token', e);
        setOpenError(true);
      });
  };

  const navigateToChat = () => {
    const messageParam = searchParams.get('message');
    const autoStartChatParam = searchParams.get('autoStartChat');
    let url = `/demochat/${companyIdentifier}`;
    if (messageParam || autoStartChatParam) {
      url += '?';
    }
    if (messageParam) {
      url += `message=${encodeURIComponent(messageParam)}&`;
    }
    if (autoStartChatParam) {
      url += `autoStartChat=${autoStartChatParam}`;
    }
    navigate(url);
  };

  useEffect(() => {
    if (createClientData && companyIdentifier) {
      setClientPhoneIdentifier(companyIdentifier, createClientData.id);
      navigateToChat();
    }
  }, [createClientData]);

  useEffect(() => {
    if (error) {
      if ('sourcealStatus' in error && error.sourcealStatus === 401) {
        setErrorMessage('No tienes permisos para iniciar un chat');
      } else {
        setErrorMessage('Ha ocurrido un error iniciando el chat');
      }
      setOpenError(true);
    }
  }, [error]);

  // If it has the autoStartChat param, then start the chat automatically
  useEffect(() => {
    const autoStartChatParam = searchParams.get('autoStartChat');
    if (autoStartChatParam) {
      // sleep for half a second so that the recaptcha is loaded
      setTimeout(() => {
        startChat();
      }, 500);
    }
  }, []);

  const handleSelectChat = (phoneIdentifier: string) => {
    // Update session/local storage with the selected chat
    setClientPhoneIdentifier(companyIdentifier, phoneIdentifier);
    navigate(`/demochat/${companyIdentifier}`, { replace: true });
    window.location.reload();
  };

  const handleDeleteChat = (phoneIdentifier: string) => {
    deleteChatFromHistory(companyIdentifier, phoneIdentifier);
    setChatHistory(getChatHistory(companyIdentifier));
  };

  return (
    <Box sx={{ height: { xs: '93vh', md: '100vh' }, display: 'flex', flexDirection: 'row' }}>
      {/* Sidebar */}
      {showSidebar && companyIdentifier && (
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <ChatHistorySidebar
            companyIdentifier={companyIdentifier}
            currentPhoneIdentifier={null}
            chatHistory={chatHistory}
            onSelectChat={handleSelectChat}
            onDeleteChat={handleDeleteChat}
            disableNewChatButton
          />
        </Box>
      )}
      {/* Main Chat Area */}
      <Box sx={{ flex: 1 }}>
        <ChatPanel>
          <ChatPanel.Header
            title={capitalizeFirstLetterOfEachWord(companyIdentifier || 'Demo Chat')}
            subtitle="En línea"
          />
          <ChatPanel.Body>
            <AlertDialog
              openDialog={openError}
              setOpenDialog={setOpenError}
              header={errorMessage}
              msg="Intenta nuevamente, con otros datos. O inicia sesión con otro usuario."
            />
            {openForm ? (
              <Stack
                spacing={1}
                sx={{
                  pt: 2,
                  m: 6,
                }}
              >
                {Object.entries(clientData)
                  .filter(([k]) => !k.includes('brand') && k !== 'source')
                  .map(([key, value]) => (
                    <TextField
                      key={key}
                      label={mapAttributes(key)}
                      value={value}
                      onChange={(e) =>
                        setClientData({ ...clientData, [key]: key === 'rut' ? format(e.target.value) : e.target.value })
                      }
                      sx={{ backgroundColor: 'white' }}
                      fullWidth
                    />
                  ))}
                {sources && sources.length > 0 && (
                  <FormControl>
                    <InputLabel>Origen</InputLabel>
                    <Select
                      value={clientData.source}
                      label="Origen"
                      sx={{ backgroundColor: 'white' }}
                      onChange={(e) => setClientData({ ...clientData, source: e.target.value })}
                    >
                      {sources.map((source) => (
                        <MenuItem key={source} value={source}>
                          {source}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {brands && brands.length > 0 && (
                  <FormControl>
                    <InputLabel>Marca de interés</InputLabel>
                    <Select
                      value={clientData.brandOfInterest}
                      label="Marca"
                      onChange={(e) => setClientData({ ...clientData, brandOfInterest: e.target.value })}
                      sx={{ backgroundColor: 'white' }}
                    >
                      {brands.map((brand) => (
                        <MenuItem key={brand} value={brand}>
                          {brand}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Button
                  variant="contained"
                  onClick={startChat}
                  sx={{
                    bgcolor: YELLOW,
                    color: NAVY,
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: RADIUS_COIN,
                    px: 3,
                    '&:hover': { bgcolor: YELLOW, opacity: 0.9 },
                  }}
                >
                  Iniciar Chat
                </Button>
              </Stack>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-evenly',
                  height: '100%',
                  width: '100%',
                  alignItems: 'center',
                }}
              >
                <Button
                  variant="contained"
                  onClick={startChat}
                  autoFocus
                  sx={{
                    bgcolor: YELLOW,
                    color: NAVY,
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: RADIUS_COIN,
                    px: 3,
                    '&:hover': { bgcolor: YELLOW, opacity: 0.9 },
                  }}
                >
                  Iniciar Chat
                </Button>
                {companyIdentifier && BUSINESS_UNITS_WITH_FORM.includes(companyIdentifier) && (
                  <Button
                    variant="contained"
                    onClick={() => setOpenForm(true)}
                    sx={{
                      bgcolor: NAVY,
                      color: '#fff',
                      fontFamily: 'Jost, sans-serif',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: RADIUS_COIN,
                      px: 3,
                      '&:hover': { bgcolor: NAVY, opacity: 0.9 },
                    }}
                  >
                    Iniciar Chat con Datos
                  </Button>
                )}

                {clientPhoneIdentifier && (
                  <Button
                    variant="contained"
                    onClick={navigateToChat}
                    sx={{
                      bgcolor: NAVY,
                      color: '#fff',
                      fontFamily: 'Jost, sans-serif',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: RADIUS_COIN,
                      px: 3,
                      '&:hover': { bgcolor: NAVY, opacity: 0.9 },
                    }}
                  >
                    Continuar chat
                  </Button>
                )}
              </Box>
            )}
          </ChatPanel.Body>
        </ChatPanel>
      </Box>
    </Box>
  );
};

export default NewDemochat;
